import ActionButton from '../common/ActionButton'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import Link from 'next/link'
import {
  FORM_TYPE_SCHEDULE_VISIT,
  TERMS_AND_CONDITIONS_PAGE,
} from '@/utils/constants'
import LOCATION_HELPER from '@/helper/locationHelper'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSelector } from '@/lib/store'
import { loadState } from '@/utils/localStorage'

const RequestCallbackForm = ({
  form,
  loading,
  onOpenChange,
  handleSubmitRequestCallback,
  handleInputChange,
  radiologyRequired = false,
  formType = '',
  citySlug = '',
  formId,
}: any) => {
  const { handleGetAllRadiologyCityListData } = LOCATION_HELPER()
  const loggedInMobileNumber = loadState('ctxVal') || null

  const cityState = useSelector((state) => state.city) || {}

  const [cityListData, setCityListData] = useState<any>([])

  const handleGetCityListing = async () => {
    const cityDataRes = await handleGetAllRadiologyCityListData({
      radiologyRequired: radiologyRequired,
    })
    if (cityDataRes?.status) {
      setCityListData(cityDataRes?.data)
      // On the campaign landing page, pre-select the city coming from the URL.
      if (citySlug) {
        const matchedCity = cityDataRes?.data?.find(
          (city: any) => city?.slugName === citySlug
        )
        if (matchedCity?.uuid) {
          handleInputChange('selectedCity', matchedCity.uuid)
          setValue('selectedCity', matchedCity.uuid)
          clearErrors('selectedCity')
        }
      }
    }
  }

  let validationSchema = yup
    .object({
      patientName: yup
        .string()
        .min(3, 'Name must be at least 3 characters')
        .required('This field is mandatory'),
      patientPhoneNumber: yup
        .string()
        .min(10, 'Phone number must be at least 10 digit')
        .matches(/^[6-9]/, 'Invalid phone number')
        .required('This field is mandatory'),
      selectedCity: yup.string().when('$formType', {
        is: (formType: string) => formType !== FORM_TYPE_SCHEDULE_VISIT,
        then: (schema) => schema.required('This field is mandatory'),
        otherwise: (schema) => schema.notRequired(),
      }),
      termsChecked: yup
        .boolean()
        .oneOf([true], 'You must accept the terms and conditions'),
    })
    .required()

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      patientName: '',
      patientPhoneNumber: loggedInMobileNumber
        ? loggedInMobileNumber.toString()
        : '',
      selectedCity: cityState?.currentCityObj?.uuid || '',
      termsChecked: false,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    context: { formType },
  })

  useEffect(() => {
    formType !== FORM_TYPE_SCHEDULE_VISIT && handleGetCityListing()
  }, [formType])

  return (
    <div>
      <form id={formId} onSubmit={handleSubmit(handleSubmitRequestCallback)}>
        <div className="grid gap-4">
          <div>
            <Label className="text-label-xs text-gray-700 mb-1">
              Full name *
            </Label>
            <Input
              {...register('patientName', { required: true })}
              type="text"
              value={form.patientName}
              placeholder="Enter full name"
              className="w-full bg-gray-100"
              onChange={(e) => {
                handleInputChange('patientName', e.target.value)
                setValue('patientName', e.target.value)
                clearErrors('patientName')
              }}
            />
            {errors.patientName?.message && (
              <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                {errors.patientName?.message}
              </p>
            )}
          </div>
          <div>
            <Label className="text-label-xs text-gray-700 mb-1">
              Enter Mobile Number *
            </Label>
            <div className="mobile_number_input">
              <Input
                {...register('patientPhoneNumber', { required: true })}
                type="number"
                value={form.patientPhoneNumber}
                className="w-full bg-gray-100 pl-12"
                onScroll={(e) => e.preventDefault()}
                // onResize={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if (
                    e.key === 'e' ||
                    e.key === 'E' ||
                    e.key === '+' ||
                    e.key === '-'
                  ) {
                    e.preventDefault()
                  }
                }}
                onChange={(e) => {
                  // Ensure the value doesn't exceed 10 digits
                  if (e.target.value.length >= 10) {
                    e.target.value = e.target.value.slice(0, 10)
                  }
                  handleInputChange('patientPhoneNumber', e.target.value)
                  setValue('patientPhoneNumber', e.target.value)
                  clearErrors('patientPhoneNumber')
                }}
              />
            </div>
            {errors.patientPhoneNumber?.message && (
              <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                {errors.patientPhoneNumber?.message}
              </p>
            )}
          </div>
          {formType != FORM_TYPE_SCHEDULE_VISIT && (
            <div>
              <Label className="text-label-xs text-gray-700 mb-1">
                City *
              </Label>
              <Select
                {...register('selectedCity', { required: true })}
                onValueChange={(data) => {
                  handleInputChange('selectedCity', data)
                  setValue('selectedCity', data)
                  clearErrors('selectedCity')
                }}
                value={
                  (form.selectedCity &&
                    cityListData?.find(
                      (item: any) => item?.uuid === form?.selectedCity
                    )?.uuid) ??
                  cityListData?.[0]?.uuid
                }
              >
                <SelectTrigger className="bg-gray-100">
                  <SelectValue placeholder="Select a city" />
                </SelectTrigger>
                <SelectContent>
                  {cityListData &&
                    cityListData?.length > 0 &&
                    cityListData?.map((city: any) => {
                      return (
                        <SelectItem key={city.uuid} value={city.uuid}>
                          {city?.name}
                        </SelectItem>
                      )
                    })}
                </SelectContent>
              </Select>
              {errors.selectedCity?.message && (
                <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                  {errors.selectedCity?.message}
                </p>
              )}
            </div>
          )}
          <div className="">
            <div className="flex gap-3 items-start">
              <Checkbox
                className="h-5 w-5 border-2 hover:ring-1 mt-0.5 flex-shrink-0"
                id="terms1"
                checked={form.termsChecked}
                onCheckedChange={(data) => {
                  const isChecked = data === true
                  setValue('termsChecked', isChecked)
                  handleInputChange('termsChecked', isChecked)
                  clearErrors('termsChecked')
                }}
              />
              <Label
                htmlFor="terms1"
                className="text-body-sm text-gray-700 leading-tight cursor-pointer"
              >
                I hereby affirm & authorise DDRC Agilus to process the personal
                data as per the{' '}
                <Link
                  className="underline"
                  href={`${TERMS_AND_CONDITIONS_PAGE}`}
                  onClick={() => onOpenChange?.(false)}
                >
                  T&C
                </Link>
                .
              </Label>
            </div>
            {errors.termsChecked?.message && (
              <p className="text-label-xs text-danger-500 mb-0 mt-1 h-4">
                {errors.termsChecked?.message}
              </p>
            )}
          </div>
          <ActionButton
            variant="primary"
            title={
              formType === FORM_TYPE_SCHEDULE_VISIT
                ? 'Submit request'
                : 'Get a call back'
            }
            type="submit"
            loading={loading}
            disabled={loading}
            className="text-label-lg font-semibold"
          />
        </div>
      </form>
    </div>
  )
}

export default RequestCallbackForm
